import React, { useState, useEffect, useCallback } from 'react';
import { ScrollText, Loader2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatarDataHora } from '../utils/formatters';

const ACOES_COR = {
  LOGIN:                    'text-blue-400 bg-blue-900/20',
  DOWNLOAD_XML:             'text-emerald-400 bg-emerald-900/20',
  DOWNLOAD_LOTE:            'text-emerald-400 bg-emerald-900/20',
  RECEBIMENTO_API_DELPHI:   'text-primary-400 bg-primary-900/20',
  RECEBIMENTO_LOTE_DELPHI:  'text-primary-400 bg-primary-900/20',
  UPLOAD_MANUAL:            'text-amber-400 bg-amber-900/20',
  DEFAULT:                  'text-slate-400 bg-slate-700/30',
};

function badgeAcao(acao) {
  const cls = ACOES_COR[acao] || ACOES_COR.DEFAULT;
  return (
    <span className={`font-mono text-xs px-2 py-0.5 rounded ${cls}`}>
      {acao}
    </span>
  );
}

export default function AuditoriaPage() {
  const [logs,    setLogs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [pagina,  setPagina]  = useState(1);
  const [paginas, setPaginas] = useState(1);
  const [loading, setLoading] = useState(true);

  const [filtros, setFiltros] = useState({ acao: '', data_inicio: '', data_fim: '' });

  const carregar = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { pagina: pg, limite: 50, ...filtros };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await api.get('/relatorios/auditoria', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setPaginas(data.paginas || 1);
      setPagina(pg);
    } catch {
      toast.error('Erro ao carregar auditoria.');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { carregar(1); }, []);

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="card space-y-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <input className="input text-sm" placeholder="Filtrar por ação..." value={filtros.acao}
            onChange={e => setFiltros(f => ({...f, acao: e.target.value.toUpperCase()}))} />
          <input type="date" className="input text-sm" value={filtros.data_inicio}
            onChange={e => setFiltros(f => ({...f, data_inicio: e.target.value}))} />
          <input type="date" className="input text-sm" value={filtros.data_fim}
            onChange={e => setFiltros(f => ({...f, data_fim: e.target.value}))} />
          <div className="flex gap-2">
            <button onClick={() => carregar(1)} className="btn-primary text-sm py-1.5 px-3 flex-1">
              Filtrar
            </button>
            <button onClick={() => { setFiltros({ acao: '', data_inicio: '', data_fim: '' }); carregar(1); }}
              className="btn-secondary text-sm py-1.5 px-3">
              <RefreshCw size={13} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border bg-surface/40">
                {['Data/Hora', 'Usuário', 'Ação', 'Empresa', 'IP', 'Detalhes'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12">
                  <Loader2 size={24} className="animate-spin text-primary-500 mx-auto" />
                </td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-slate-600">
                  <ScrollText size={32} className="mx-auto mb-2 opacity-30" />
                  Nenhum registro encontrado
                </td></tr>
              ) : logs.map(log => (
                <tr key={log.id} className="hover:bg-surface-card/50 transition-colors">
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{formatarDataHora(log.criado_em)}</td>
                  <td className="px-4 py-3">
                    <p className="text-slate-300 text-xs">{log.usuario_nome || 'Sistema/API'}</p>
                  </td>
                  <td className="px-4 py-3">{badgeAcao(log.acao)}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs max-w-[150px] truncate">{log.empresa_nome || '—'}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">{log.ip || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs max-w-[200px] truncate">
                    {log.detalhes && Object.keys(log.detalhes).length > 0
                      ? Object.entries(log.detalhes).map(([k, v]) => `${k}: ${v}`).join(' | ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {paginas > 1 && (
          <div className="px-4 py-3 border-t border-surface-border flex items-center justify-between">
            <p className="text-xs text-slate-600">Página {pagina} de {paginas}</p>
            <div className="flex gap-1">
              <button onClick={() => carregar(pagina - 1)} disabled={pagina === 1}
                className="p-1.5 rounded-lg text-slate-500 hover:text-white disabled:opacity-30 hover:bg-surface-card">
                <ChevronLeft size={16} />
              </button>
              <button onClick={() => carregar(pagina + 1)} disabled={pagina === paginas}
                className="p-1.5 rounded-lg text-slate-500 hover:text-white disabled:opacity-30 hover:bg-surface-card">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
